import { Fragment } from 'react'
import { Plane } from 'lucide-react'
import { Card, Row } from '../ui'
import { days, placeName, type Period } from '../lib/calendar'
import { fmtDayShort } from '../lib/today'
import { placeEmoji } from '../lib/placeEmoji'
import { homeName, useRegionsNow } from '../lib/regions'
import { useProfile } from '../lib/profile'
import type { YearItem } from '../lib/yearItems'
import { DatePlate } from './DatePlate'
import { RowMore } from './RowMore'

/** The shading the Year grid used to give and the timeline never had (WP 5.6):
 *  the rail's segments read it too, which is why it is exported rather than
 *  copied — the dot on a card and the segment beside it are the same place. */
export const PLACE_COLOUR: Record<string, string> = {
  Home: 'var(--ink-3)',
  Home2: 'var(--p-home2)',
  Travel: 'var(--p-travel)',
}

/** A row's plate colour, by what the row is (WP 10.5). Gold for a
 *  birthday — the celebratory accent `.pitem.bday-today` already uses — and
 *  the trip hue for a flight, since a flight is how a trip begins and ends.
 *  Everything else is Doug's own plan and draws on plain `--paper-2`: a
 *  colour for every kind would make the column read as a legend. A `Map`,
 *  not an object literal, so a kind is never answered by
 *  `Object.prototype` (CLAUDE.md's alias-map bug). */
const PLATE_HUE = new Map<YearItem['kind'], string>([
  ['birthday', 'var(--p-visit)'],
  ['flight', 'var(--p-travel)'],
])

/**
 * A stay or a trip, place in `--t-title` serif with the flag, the date range
 * and the length in `--t-meta`, everything inside it — flights, holidays,
 * commitments, birthdays, tasks and tentative saved events — as one sorted
 * stream of `ui/Row`s underneath (Y4).
 *
 * Extracted out of `Year.tsx` (WP 4.7). It no longer merges
 * commitments/flights/holidays itself — `yearItems.ts` does that once, and
 * this only renders whatever slice of the result its caller hands it.
 *
 * WP 6.5 moved it again, off the retired scaled rail and into the month
 * ribbon's own stack of cards (`MonthRibbon.tsx`) — one per period, in flow,
 * no longer load-bearing for a layout that reserved pixels for it. A visitor
 * used to show here as an inline "overlap" chip read off `stays.kind =
 * 'visit'`; visitors are now their own table and their own card
 * (`VisitorCard`), rendered by the caller between period cards rather than
 * inside one, so this reads only `Period` and `YearItem`s and nothing of
 * `stays` beyond what `Period` already carries.
 */
export function PeriodCard({
  p, today, items, second, secondCountry,
  onEditPeriod, onEditItem, onRemoveItem,
}: {
  p: Period
  today: string
  /** The profile's second home, for what `Home2` is called and flagged (U1.3). */
  second?: { label: string } | null
  secondCountry?: string | null
  /** Pre-filtered to `[p.start, p.end]` and sorted by date; the caller (the
   *  month view) decides the slice. */
  items: YearItem[]
  /** Absent for home, which is not a stay but the absence of one. */
  onEditPeriod?: () => void
  /** Only ever called for a `commitment` or `flight` item — nothing else on
   *  this card came from a row Year owns. */
  onEditItem?: (item: YearItem) => void
  onRemoveItem?: (item: YearItem) => void
}) {
  // C2.1: what a `'Home'` period is called — the home region's name.
  const home = homeName(useRegionsNow(), useProfile())
  const isNow = today >= p.start && today <= p.end
  const length = days(p.start, p.end) + 1
  const daysLeft = days(today, p.end)
  const editable = (kind: YearItem['kind']) => kind === 'commitment' || kind === 'flight'

  // WP 10.7 (10.5's own follow-up): the start date now has a face — the same
  // `DatePlate` every row below already carries — so the header only needs
  // to say where the stay ends. A single day says nothing more than the
  // plate already does.
  const until = p.start === p.end
    ? ''
    : `until ${fmtDayShort(p.end)}`

  const headInner = (
    <>
      {/* For travel the label IS the destination, so it becomes the heading.
          Elsewhere it is a note on a stay — "Study week" is not a place. */}
      <span className="yr-place">
        <i className="yr-dot" style={{ background: PLACE_COLOUR[p.place] }} aria-hidden="true" />
        {placeEmoji(p, secondCountry)} {placeName(p, second, home)}
      </span>
      <span className="yr-dates">{until}</span>
    </>
  )

  return (
    <Card
      className={`yr-period${isNow ? ' is-now' : ''}${p.provisional ? ' is-soft' : ''}${p.place === 'Home' ? ' is-quiet' : ''}`}
      id={`p${p.start}`}
    >
      {/* WP 10.7: `.yr-head` still owns the column's own padding and gap —
          this is its one child now, a plain inline row (no new CSS file is
          in this package's brief) putting the plate beside the text stack
          `.yr-head__btn`/`.yr-len` already were. */}
      <div className="yr-head">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          {/* The same hue as the header's own dot (`PLACE_COLOUR`), not
              `PLATE_HUE` — that map is keyed by an item's *kind*
              (birthday/flight), and a period is a place, not a kind. Plain
              `--ink-3` (Berlin) reads as no colour at all against the
              plate's own `--paper-2`, which is the point for `is-quiet`. */}
          <DatePlate on={p.start} hue={PLACE_COLOUR[p.place]} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {onEditPeriod ? (
              <button type="button" className="yr-head__btn" onClick={onEditPeriod}>{headInner}</button>
            ) : (
              <div className="yr-head__btn">{headInner}</div>
            )}
            <div className="yr-len">
              <b>{length} day{length === 1 ? '' : 's'}</b>
              {isNow && ' · you are here'}
              {p.place === 'Travel' && ' · trip'}
              {p.label && p.place !== 'Travel' && ` · ${p.label}`}
              {p.provisional && ' · provisional'}
              {p.open && items.length === 0 && ' · nothing planned'}
            </div>
          </div>
        </div>
      </div>
      {(isNow || items.length > 0) && (
        <div className="yr-rows">
          {/* WP 10.5: every row's date is its plate, at the left; the meta
              line keeps only what the date line used to trail. */}
          {isNow && (
            <Row className="u-row--list" leading={<DatePlate on={today} />}
              title="Today" meta={`${daysLeft} day${daysLeft === 1 ? '' : 's'} left`} />
          )}
          {items.map((item, i) => (
            <Fragment key={item.id}>
              {i > 0 && item.on.slice(0, 4) !== items[i - 1].on.slice(0, 4) && (
                <div className="yearsep"><span>{item.on.slice(0, 4)}</span></div>
              )}
              <Row
                className="u-row--list"
                leading={
                  <DatePlate on={item.on} hue={PLATE_HUE.get(item.kind)}
                    icon={item.kind === 'flight' ? <Plane size={14} /> : undefined} />
                }
                title={item.title}
                meta={item.fact ?? null}
                onPress={editable(item.kind) && onEditItem ? () => onEditItem(item) : undefined}
                swipe={editable(item.kind) && onRemoveItem
                  ? { left: { label: 'Remove', onAction: () => onRemoveItem(item), leaves: true } }
                  : undefined}
                // a2-32: the swipe's Remove, and the tap's Edit, behind a
                // "…" you can see — on the rows Year owns (a day's plan, a
                // flight) and nowhere else, the same test as the swipe.
                trailing={editable(item.kind) && (onEditItem || onRemoveItem) ? (
                  <RowMore
                    name={item.title}
                    onEdit={onEditItem ? () => onEditItem(item) : undefined}
                    onRemove={onRemoveItem ? () => onRemoveItem(item) : undefined}
                  />
                ) : undefined}
              />
            </Fragment>
          ))}
        </div>
      )}
    </Card>
  )
}
